import { useApiRef } from '@/components/Functional/hooks-core/useApi'
import { ApiError } from '@/components/Functional/lib/request'
import type { QuestionnaireAnswerFormData } from '@/schemas/forms/questionnaire'
import type { QuestionnaireData } from '@/schemas/v2'
import type { MynaviNearbyOpenCampusItem } from '@/schemas/v2/mynavi'
import { useTokenStore } from '@/store/useTokenStore'
import { useQueryClient } from '@tanstack/react-query'
import { useMachine } from '@xstate/react'
import { useEffect, useMemo, useRef } from 'react'
import { assign, fromPromise, setup, waitFor } from 'xstate'

type GPSResult = {
  lat: number
  lon: number
  available: boolean
}

// ─── Filter Types ─────────────────────────────────────────────────────────

export type FilterId = 'schoolType' | 'area' | 'subject' | 'occupation' | 'period'
export const FILTER_IDS: readonly FilterId[] = ['schoolType', 'area', 'subject', 'occupation', 'period'] as const

export type FilterConditions = {
  collegeTypes: string[]
  prefectures: string[]
  studyFieldIds: number[]
  jobCategoryIds: number[]
  eventDateFrom: string | null
  eventDateTo: string | null
  wishedOnly: boolean
}

const initialFilterConditions: FilterConditions = {
  collegeTypes: [],
  prefectures: [],
  studyFieldIds: [],
  jobCategoryIds: [],
  eventDateFrom: null,
  eventDateTo: null,
  wishedOnly: false,
}

// ─── Context ──────────────────────────────────────────────────────────────

type MynaviContext = {
  isAuthenticated: boolean
  isAuthInitialized: boolean

  /** アンケートフロータイプ（checking 中に判定） */
  questionnaireType: 'login' | 'exchange' | null

  /** アンケートデータ（未取得は null） */
  questionnaire: QuestionnaireData | null

  /** アンケート確認画面に渡す入力値 */
  pendingQuestionnaireAnswers: QuestionnaireAnswerFormData | null

  /** OC 参加時に押印するスタンプ ID（openCampusItem.stamp.id） */
  pendingStampId: number | null

  /** 学校詳細画面に表示する学校 ID */
  selectedCollegeId: number | null

  errorMessage: string | null

  /** フィルター条件（各フィルター選択内容） */
  filterConditions: FilterConditions

  /** 現在開いているフィルターオーバーレイ（null = 非表示） */
  openFilterId: FilterId | null

  /** テキスト検索クエリ（TOP ページの学校名入力値） */
  searchQuery: string

  /** GPS フロー：取得した近くの OC 一覧 */
  nearbyOpenCampuses: MynaviNearbyOpenCampusItem[]

  /** GPS フロー：取得した緯度（nearbyOcFetching で使用） */
  gpsLat: number | null

  /** GPS フロー：取得した経度（nearbyOcFetching で使用） */
  gpsLon: number | null

  /** 1日1スタンプ制限エラー時のAPIメッセージ */
  stampAlreadyAcquiredTodayMessage: string | null
}

// ─── Input ────────────────────────────────────────────────────────────────

type MynaviInput = {
  isAuthenticated: boolean
  isAuthInitialized: boolean
}

// ─── Events ───────────────────────────────────────────────────────────────

type MynaviEvent =
  // 認証確定イベント（waitingForAuth → fetching）
  | { type: 'AUTH_READY'; isAuthenticated: boolean }

  // アンケートフロー
  // 注: ログインアンケートは initialLoading.fetching で自動発火するため QUESTIONNAIRE_TRIGGER は不要。
  // 将来的に交換アンケート（type='exchange'）を top.idle から手動発火する場合は復活させる。
  | { type: 'QUESTIONNAIRE_FORM_SUBMIT'; answers: QuestionnaireAnswerFormData }
  | { type: 'QUESTIONNAIRE_SKIP' }

  // OC 参加フロー（スタンプ押印）
  // UI 側で openCampusItem.stamp.id を取り出して stampId として渡す
  | { type: 'OC_PARTICIPATE_REQUEST'; stampId: number }
  | { type: 'OC_PARTICIPATE_CONFIRM' }
  | { type: 'OC_PARTICIPATE_CANCEL' }

  // OC 参加フロー（GPS 経由・TOP ページ「参加登録」ボタン）
  | { type: 'OC_REGISTER_TAPPED' }
  | { type: 'OC_NEARBY_SELECT'; stampId: number }
  | { type: 'OC_GPS_ERROR_CLOSE' }
  | { type: 'OC_NO_NEARBY_CLOSE' }
  | { type: 'OC_ALREADY_PARTICIPATED_CLOSE' }
  | { type: 'OC_STAMP_ALREADY_ACQUIRED_TODAY_CLOSE' }

  // 画面遷移
  | { type: 'NAVIGATE_COLLEGE_DETAIL'; collegeId: number }
  | { type: 'NAVIGATE_BACK' }

  // フィルターオーバーレイ
  | { type: 'FILTER_OPEN'; filterId: FilterId }
  | { type: 'FILTER_CLOSE' }
  | { type: 'FILTER_CLEAR'; filterId: FilterId }
  | { type: 'FILTER_SELECT_COLLEGE_TYPES'; collegeTypes: string[] }
  | { type: 'FILTER_SELECT_AREA'; prefectures: string[] }
  | { type: 'FILTER_SELECT_STUDY_FIELDS'; studyFieldIds: number[] }
  | { type: 'FILTER_SELECT_JOB_CATEGORIES'; jobCategoryIds: number[] }
  | { type: 'FILTER_SELECT_PERIOD'; from: string | null; to: string | null }
  | { type: 'FILTER_SELECT_WISHED_ONLY'; wishedOnly: boolean }

  // 検索結果ページ遷移
  | { type: 'NAVIGATE_SEARCH_RESULT'; query: string }

  // 共通
  | { type: 'RETRY' }

// ─── Machine Base ─────────────────────────────────────────────────────────

const mynaviStateMachineBase = setup({
  types: {
    context: {} as MynaviContext,
    events: {} as MynaviEvent,
    input: {} as MynaviInput,
  },
  actors: {
    fetchQuestionnaire: fromPromise<QuestionnaireData | null>(async () => null),
    postQuestionnaireAnswer: fromPromise<
      void,
      { questionnaire: QuestionnaireData; answers: QuestionnaireAnswerFormData }
    >(async () => undefined),
    postOcParticipate: fromPromise<void, { stampId: number; lat: number | null; lon: number | null }>(
      async () => undefined,
    ),
    fetchGPS: fromPromise<GPSResult, void>(async () => ({ lat: 0, lon: 0, available: false })),
    fetchNearbyOcList: fromPromise<MynaviNearbyOpenCampusItem[], { lat: number; lon: number }>(async () => []),
  },
  guards: {
    isAuthInitialized: ({ context }) => context.isAuthInitialized,
    isAuthenticated: ({ context }) => context.isAuthenticated,
    hasQuestionnaire: ({ context }) => context.questionnaire !== null,
  },
}).createMachine({
  id: 'mynavi',
  initial: 'initialLoading',
  context: ({ input }) => ({
    isAuthenticated: input.isAuthenticated,
    isAuthInitialized: input.isAuthInitialized,
    questionnaireType: null,
    questionnaire: null,
    pendingQuestionnaireAnswers: null,
    pendingStampId: null,
    selectedCollegeId: null,
    errorMessage: null,
    filterConditions: initialFilterConditions,
    openFilterId: null,
    searchQuery: '',
    nearbyOpenCampuses: [],
    gpsLat: null,
    gpsLon: null,
    stampAlreadyAcquiredTodayMessage: null,
  }),

  states: {
    // ──────────────────────────────────────────────────────
    // 初期ローディング（認証確定後 → top へ）
    // ──────────────────────────────────────────────────────
    initialLoading: {
      initial: 'waitingForAuth',
      states: {
        waitingForAuth: {
          // input.isAuthInitialized が true（getState() で確定済み）なら即スキップ
          always: [{ guard: 'isAuthInitialized', target: 'fetching' }],
          on: {
            AUTH_READY: {
              target: 'fetching',
              actions: assign({
                isAuthenticated: ({ event }) => event.isAuthenticated,
                isAuthInitialized: () => true,
              }),
            },
          },
        },
        fetching: {
          // 認証済みの場合のみ初回ログインアンケートを取得する
          always: [{ guard: 'isAuthenticated', target: 'fetchingQuestionnaire' }, { target: '#mynavi.top.idle' }],
        },
        fetchingQuestionnaire: {
          // fetchQuestionnairesType('register') を呼び出す。
          // answered === true の場合は null を返す（スキップ）。
          invoke: {
            src: 'fetchQuestionnaire',
            onDone: [
              {
                // アンケートあり（未回答）→ 即フォーム表示
                guard: 'hasQuestionnaire',
                target: '#mynavi.top.questionnaire.form',
                actions: assign({
                  questionnaire: ({ event }) => event.output,
                  questionnaireType: () => 'login' as const,
                }),
              },
              // なし or 回答済み → 通常 TOP へ
              { target: '#mynavi.top.idle' },
            ],
            onError: {
              // 取得失敗は無視して通常 TOP へ（アンケート非表示）
              target: '#mynavi.top.idle',
            },
          },
        },
      },
    },

    // ──────────────────────────────────────────────────────
    // TOP ページ
    // ──────────────────────────────────────────────────────
    top: {
      initial: 'idle',
      states: {
        idle: {
          on: {
            OC_PARTICIPATE_REQUEST: {
              target: 'openCampusParticipation.confirmDialog',
              actions: assign({
                pendingStampId: ({ event }) => event.stampId,
                errorMessage: null,
              }),
            },
            OC_REGISTER_TAPPED: {
              target: 'openCampusParticipation',
              actions: assign({ errorMessage: null }),
            },
            FILTER_OPEN: {
              actions: assign({ openFilterId: ({ event }) => event.filterId }),
            },
            FILTER_CLOSE: {
              actions: assign({ openFilterId: null }),
            },
            FILTER_CLEAR: {
              actions: assign({
                openFilterId: null,
                filterConditions: ({ context, event }) => {
                  switch (event.filterId) {
                    case 'schoolType':
                      return { ...context.filterConditions, collegeTypes: [] }
                    case 'area':
                      return { ...context.filterConditions, prefectures: [] }
                    case 'subject':
                      return { ...context.filterConditions, studyFieldIds: [] }
                    case 'occupation':
                      return { ...context.filterConditions, jobCategoryIds: [] }
                    case 'period':
                      return { ...context.filterConditions, eventDateFrom: null, eventDateTo: null }
                    default:
                      return context.filterConditions
                  }
                },
              }),
            },
            FILTER_SELECT_COLLEGE_TYPES: {
              actions: assign({
                openFilterId: null,
                filterConditions: ({ context, event }) => ({
                  ...context.filterConditions,
                  collegeTypes: event.collegeTypes,
                }),
              }),
            },
            FILTER_SELECT_AREA: {
              actions: assign({
                openFilterId: null,
                filterConditions: ({ context, event }) => ({
                  ...context.filterConditions,
                  prefectures: event.prefectures,
                }),
              }),
            },
            FILTER_SELECT_STUDY_FIELDS: {
              actions: assign({
                openFilterId: null,
                filterConditions: ({ context, event }) => ({
                  ...context.filterConditions,
                  studyFieldIds: event.studyFieldIds,
                }),
              }),
            },
            FILTER_SELECT_JOB_CATEGORIES: {
              actions: assign({
                openFilterId: null,
                filterConditions: ({ context, event }) => ({
                  ...context.filterConditions,
                  jobCategoryIds: event.jobCategoryIds,
                }),
              }),
            },
            FILTER_SELECT_PERIOD: {
              actions: assign({
                openFilterId: null,
                filterConditions: ({ context, event }) => ({
                  ...context.filterConditions,
                  eventDateFrom: event.from,
                  eventDateTo: event.to,
                }),
              }),
            },
            FILTER_SELECT_WISHED_ONLY: {
              actions: assign({
                filterConditions: ({ context, event }) => ({
                  ...context.filterConditions,
                  wishedOnly: event.wishedOnly,
                }),
              }),
            },
            NAVIGATE_SEARCH_RESULT: {
              target: '#mynavi.searchResult',
              actions: assign({
                searchQuery: ({ event }) => event.query,
              }),
            },
          },
        },

        questionnaire: {
          initial: 'form',
          states: {
            // アンケートフォーム表示（initialLoading.fetchingQuestionnaire から直接遷移）
            form: {
              on: {
                QUESTIONNAIRE_FORM_SUBMIT: {
                  target: 'submitting',
                  actions: assign({
                    pendingQuestionnaireAnswers: ({ event }) => event.answers,
                  }),
                },
                QUESTIONNAIRE_SKIP: '#mynavi.top.idle',
              },
            },

            submitting: {
              invoke: {
                src: 'postQuestionnaireAnswer',
                input: ({ context }) => ({
                  questionnaire: context.questionnaire!,
                  answers: context.pendingQuestionnaireAnswers!,
                }),
                onDone: {
                  // 送信完了 → 即 idle へ戻る
                  target: '#mynavi.top.idle',
                  actions: assign({
                    questionnaire: null,
                    pendingQuestionnaireAnswers: null,
                    questionnaireType: null,
                  }),
                },
                onError: {
                  target: 'form',
                  actions: assign({ errorMessage: ({ event }) => String(event.error) }),
                },
              },
            },
          },
        },

        openCampusParticipation: {
          initial: 'gpsAcquiring',
          states: {
            // ── GPS 取得中（「参加登録」ボタン経由のみ通過） ───────────
            gpsAcquiring: {
              invoke: {
                src: 'fetchGPS',
                onDone: [
                  {
                    guard: ({ event }) => event.output.available,
                    target: 'nearbyOcFetching',
                    actions: assign({
                      gpsLat: ({ event }) => event.output.lat,
                      gpsLon: ({ event }) => event.output.lon,
                    }),
                  },
                  { target: 'gpsError' },
                ],
                onError: { target: 'gpsError' },
              },
            },

            // ── 近くの OC 一覧取得中 ──────────────────────────────────
            nearbyOcFetching: {
              invoke: {
                src: 'fetchNearbyOcList',
                input: ({ context }) => ({ lat: context.gpsLat!, lon: context.gpsLon! }),
                onDone: [
                  // 0 件 → 「見つからない」ダイアログ
                  { guard: ({ event }) => event.output.length === 0, target: 'noNearbyOc' },
                  // 1 件 かつ stamp.id が有効 → 選択ダイアログをスキップして参加確認ダイアログへ
                  {
                    guard: ({ event }) => event.output.length === 1 && event.output[0].stamp.id != null,
                    target: 'confirmDialog',
                    actions: assign({
                      nearbyOpenCampuses: ({ event }) => event.output,
                      pendingStampId: ({ event }) => event.output[0].stamp.id!,
                    }),
                  },
                  // 複数件 or 1 件でも stamp.id が null → OC 選択ダイアログ
                  {
                    target: 'nearbyOcDialog',
                    actions: assign({ nearbyOpenCampuses: ({ event }) => event.output }),
                  },
                ],
                onError: [
                  // 当日スタンプ取得済み → 専用ダイアログへ
                  {
                    guard: ({ event }) => {
                      if (!(event.error instanceof ApiError)) return false
                      const err = event.error as ApiError
                      return err.status === 400 && err.code === 'STAMP_ALREADY_ACQUIRED_TODAY'
                    },
                    target: 'stampAlreadyAcquiredToday',
                    actions: assign({
                      stampAlreadyAcquiredTodayMessage: ({ event }) => {
                        const err = event.error as ApiError
                        const body = err.body as { error?: { message?: string } } | undefined
                        return body?.error?.message ?? null
                      },
                    }),
                  },
                  { target: 'noNearbyOc' },
                ],
              },
            },

            // ── 複数 OC 選択ダイアログ ────────────────────────────────
            nearbyOcDialog: {
              on: {
                OC_NEARBY_SELECT: {
                  target: 'confirmDialog',
                  actions: assign({
                    pendingStampId: ({ event }) => event.stampId,
                  }),
                },
                OC_PARTICIPATE_CANCEL: {
                  target: '#mynavi.top.idle',
                  actions: assign({ nearbyOpenCampuses: [], pendingStampId: null, gpsLat: null, gpsLon: null }),
                },
              },
            },

            // ── 参加確認ダイアログ（既存 + OC_PARTICIPATE_REQUEST 経由もここへ） ─
            confirmDialog: {
              on: {
                OC_PARTICIPATE_CONFIRM: 'participating',
                OC_PARTICIPATE_CANCEL: {
                  target: '#mynavi.top.idle',
                  actions: assign({ pendingStampId: null, nearbyOpenCampuses: [], gpsLat: null, gpsLon: null }),
                },
              },
            },

            // ── スタンプ押印 API 呼び出し中 ───────────────────────────
            participating: {
              invoke: {
                src: 'postOcParticipate',
                input: ({ context }) => ({
                  stampId: context.pendingStampId!,
                  lat: context.gpsLat,
                  lon: context.gpsLon,
                }),
                onDone: 'done',
                onError: [
                  {
                    guard: ({ event }) => event.error instanceof ApiError && (event.error as ApiError).status === 409,
                    target: 'alreadyParticipated',
                  },
                  {
                    guard: ({ event }) => {
                      if (!(event.error instanceof ApiError)) return false
                      const err = event.error as ApiError
                      if (err.status !== 400) return false
                      const body = err.body as { error?: { code?: string } } | undefined
                      return body?.error?.code === 'STAMP_ALREADY_ACQUIRED'
                    },
                    target: 'alreadyParticipated',
                  },
                  {
                    guard: ({ event }) => {
                      if (!(event.error instanceof ApiError)) return false
                      const err = event.error as ApiError
                      return err.status === 400 && err.code === 'STAMP_ALREADY_ACQUIRED_TODAY'
                    },
                    target: 'stampAlreadyAcquiredToday',
                    actions: assign({
                      stampAlreadyAcquiredTodayMessage: ({ event }) => {
                        const err = event.error as ApiError
                        const body = err.body as { error?: { message?: string } } | undefined
                        return body?.error?.message ?? null
                      },
                    }),
                  },
                  {
                    target: 'confirmDialog',
                    actions: assign({ errorMessage: ({ event }) => String(event.error) }),
                  },
                ],
              },
            },

            // ── 参加完了（自動的に idle へ戻る） ─────────────────────
            done: {
              always: {
                target: '#mynavi.top.idle',
                actions: assign({ pendingStampId: null, nearbyOpenCampuses: [], gpsLat: null, gpsLon: null }),
              },
            },

            // ── 参加済みダイアログ（ApiError 409） ────────────────────
            alreadyParticipated: {
              on: {
                OC_ALREADY_PARTICIPATED_CLOSE: {
                  target: '#mynavi.top.idle',
                  actions: assign({ pendingStampId: null, nearbyOpenCampuses: [], gpsLat: null, gpsLon: null }),
                },
              },
            },

            // ── 1日1スタンプ制限ダイアログ（400 STAMP_ALREADY_ACQUIRED_TODAY） ──
            stampAlreadyAcquiredToday: {
              on: {
                OC_STAMP_ALREADY_ACQUIRED_TODAY_CLOSE: {
                  target: '#mynavi.top.idle',
                  actions: assign({
                    pendingStampId: null,
                    nearbyOpenCampuses: [],
                    gpsLat: null,
                    gpsLon: null,
                    stampAlreadyAcquiredTodayMessage: null,
                  }),
                },
              },
            },

            // ── 近くの OC なしダイアログ ──────────────────────────────
            noNearbyOc: {
              on: {
                OC_NO_NEARBY_CLOSE: {
                  target: '#mynavi.top.idle',
                  actions: assign({ pendingStampId: null, nearbyOpenCampuses: [], gpsLat: null, gpsLon: null }),
                },
              },
            },

            // ── GPS エラーダイアログ ───────────────────────────────────
            gpsError: {
              on: {
                OC_GPS_ERROR_CLOSE: {
                  target: '#mynavi.top.idle',
                  actions: assign({ pendingStampId: null, nearbyOpenCampuses: [], gpsLat: null, gpsLon: null }),
                },
              },
            },
          },
        },
      },
    },

    // ──────────────────────────────────────────────────────
    // 他ページ状態（将来の詳細フロー追加用プレースホルダー）
    // ──────────────────────────────────────────────────────
    searchResult: {
      on: {
        NAVIGATE_COLLEGE_DETAIL: {
          target: 'collegeDetail',
          actions: assign({
            selectedCollegeId: ({ event }) => event.collegeId,
          }),
        },
        NAVIGATE_BACK: '#mynavi.top.idle',
      },
    },
    collegeDetail: {
      on: {
        NAVIGATE_BACK: {
          target: 'searchResult',
          actions: assign({ selectedCollegeId: null }),
        },
      },
    },
    pointHistory: {},
    pointExchange: {},
    other: {},
  },
})

// ─── Hook ─────────────────────────────────────────────────────────────────

export const useMynaviStateMachine = () => {
  // useApiRef() は毎レンダーで ref.current を最新の BoundApi に更新する
  // useMemo 内の actor から apiRef.current.auth.xxx() で最新ヘッダーを参照できる
  const apiRef = useApiRef()
  const queryClient = useQueryClient()
  const queryClientRef = useRef(queryClient)
  queryClientRef.current = queryClient

  // biome-ignore lint/correctness/useExhaustiveDependencies: actors use refs
  const machine = useMemo(
    () =>
      mynaviStateMachineBase.provide({
        actors: {
          fetchQuestionnaire: fromPromise(async () => {
            // type='login' → 'register'（会員登録時アンケート）で固定
            const result = await apiRef.current.auth.fetchQuestionnairesType('register')
            // answered 済みまたはアンケートなしはスキップ（null を返す）
            if (!result.questionnaire || result.questionnaire.answered) return null
            return result.questionnaire
          }),

          postQuestionnaireAnswer: fromPromise(async ({ input: actorInput }) => {
            const questionnaireId = actorInput.questionnaire.id
            const answers = Object.entries(actorInput.answers).map(([itemId, value]) => ({
              itemId: Number.parseInt(itemId, 10),
              answer: Array.isArray(value) ? value : value ? [value] : [],
            }))
            await apiRef.current.auth.postQuestionnaireAnswer(questionnaireId, {
              questionnaireId,
              answers,
            })
          }),

          postOcParticipate: fromPromise(async ({ input: actorInput }) => {
            // OC 参加 = 対応するスタンプの押印（GPS 情報があれば送信）
            const body: Record<string, unknown> = {}
            if (actorInput.lat !== null && actorInput.lon !== null) {
              body.lat = actorInput.lat
              body.lon = actorInput.lon
            }
            await apiRef.current.auth.postStampStrict(actorInput.stampId, body)
            // 参加登録成功後に総獲得ポイント（user/sheets）を再取得する
            queryClientRef.current.invalidateQueries({ queryKey: ['mynavi', 'user-sheets'] })
          }),

          fetchGPS: fromPromise(async () => {
            return new Promise<GPSResult>((resolve) => {
              if (typeof navigator === 'undefined' || !navigator.geolocation) {
                resolve({ lat: 0, lon: 0, available: false })
                return
              }
              navigator.geolocation.getCurrentPosition(
                ({ coords }) => resolve({ lat: coords.latitude, lon: coords.longitude, available: true }),
                () => resolve({ lat: 0, lon: 0, available: false }),
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
              )
            })
          }),

          fetchNearbyOcList: fromPromise(async ({ input: actorInput }) => {
            const result = await apiRef.current.auth.fetchMynaviOpenCampuses(actorInput.lat, actorInput.lon)
            return result.openCampuses
          }),
        },
      }),
    [],
  )

  const [state, send, actorRef] = useMachine(machine, {
    input: (() => {
      // getState() はレンダーを待たず同期的に現在の store 値を返す
      const tokenState = useTokenStore.getState()
      return {
        isAuthenticated: !tokenState.loading && tokenState.idToken !== null,
        isAuthInitialized: !tokenState.loading,
      }
    })(),
  })

  // loading 解消時のフォールバック（getState() 時点でまだ loading だったケース）
  const idToken = useTokenStore((s) => s.idToken)
  const tokenLoading = useTokenStore((s) => s.loading)
  useEffect(() => {
    if (tokenLoading) return
    send({ type: 'AUTH_READY', isAuthenticated: idToken !== null })
  }, [tokenLoading, idToken, send])

  return {
    state,
    send,
    actorRef,
    // アンケート送信完了を DialogQuestionnaire が await できるよう waitFor ヘルパーを提供
    waitForQuestionnaireSubmitted: () => waitFor(actorRef, (s) => !s.matches({ top: { questionnaire: 'submitting' } })),
    // ローディング（waitingForAuth / fetching / fetchingQuestionnaire）
    isLoading: state.matches('initialLoading'),
    // TOP サブ状態
    isTopIdle: state.matches({ top: 'idle' }),
    isQuestionnaireForm: state.matches({ top: { questionnaire: 'form' } }),
    isQuestionnaireSubmitting: state.matches({ top: { questionnaire: 'submitting' } }),
    isOcConfirmDialog: state.matches({ top: { openCampusParticipation: 'confirmDialog' } }),
    isOcParticipating: state.matches({ top: { openCampusParticipation: 'participating' } }),
    isOcGpsAcquiring: state.matches({ top: { openCampusParticipation: 'gpsAcquiring' } }),
    isOcNearbyOcFetching: state.matches({ top: { openCampusParticipation: 'nearbyOcFetching' } }),
    isOcNearbyOcDialog: state.matches({ top: { openCampusParticipation: 'nearbyOcDialog' } }),
    isOcAlreadyParticipated: state.matches({ top: { openCampusParticipation: 'alreadyParticipated' } }),
    isOcStampAlreadyAcquiredToday: state.matches({ top: { openCampusParticipation: 'stampAlreadyAcquiredToday' } }),
    isOcNoNearbyOc: state.matches({ top: { openCampusParticipation: 'noNearbyOc' } }),
    isOcGpsError: state.matches({ top: { openCampusParticipation: 'gpsError' } }),
    // ページ遷移状態
    isSearchResult: state.matches('searchResult'),
    isCollegeDetail: state.matches('collegeDetail'),
    // context
    questionnaire: state.context.questionnaire,
    pendingQuestionnaireAnswers: state.context.pendingQuestionnaireAnswers,
    pendingStampId: state.context.pendingStampId,
    selectedCollegeId: state.context.selectedCollegeId,
    errorMessage: state.context.errorMessage,
    // フィルター条件
    filterConditions: state.context.filterConditions,
    openFilterId: state.context.openFilterId,
    // 検索クエリ
    searchQuery: state.context.searchQuery,
    nearbyOpenCampuses: state.context.nearbyOpenCampuses,
    isAuthenticated: state.context.isAuthenticated,
    stampAlreadyAcquiredTodayMessage: state.context.stampAlreadyAcquiredTodayMessage,
  }
}
